import { createRouter, createWebHistory } from "vue-router";
import { useRouteLoading } from "@/composables/useRouteLoading";
import HomeView from "@/views/NonLogged-In/HomeView.vue";
import LoginView from "@/views/LoginView.vue";
import PasswordResetRequestView from "@/views/PasswordResetRequestView.vue";
import PasswordResetView from "@/views/PasswordResetView.vue";
import StudentRegisterView from "@/views/Student/Registration/StudentRegisterView.vue";
import TutorRegisterView from "@/views/Tutor/Registration/TutorRegisterView.vue";
import EmailVerificationView from "@/views/EmailVerificationView.vue";
import AboutView from "@/views/NonLogged-In/AboutView.vue";
import RegisterRoleView from "@/views/RegisterRoleView.vue";
import PrivacyPolicyView from "@/views/NonLogged-In/PrivacyPolicyView.vue";
import TermsandConditionsView from "@/views/NonLogged-In/TermsandConditionsView.vue";
import FindTutorsView from "@/views/Student/FindTutorsView.vue";
import StudentMessagesView from "@/views/Student/StudentMessagesView.vue";
import TutorMessagesView from "@/views/Tutor/TutorMessagesView.vue";
import StudentSessionView from "@/views/Student/StudentSessionView.vue";
import StudentStudyMaterialsView from "@/views/Student/StudentStudyMaterialsView.vue";
import AIGeneratedStudyMaterialView from "@/views/Student/AIGeneratedStudyMaterialView.vue";
import AIGeneratedFlashcardsView from "@/views/Student/AIGeneratedFlashcardsView.vue";
import TutorSessionView from "@/views/Tutor/TutorSessionView.vue";

import TutorApplicationStatusView from "@/views/Tutor/TutorApplicationStatusView.vue";
import ResubmissionView from "@/views/Tutor/ResubmissionView.vue";

// Lazy load admin views for better performance - load on demand
const Dashboard = () => import("@/views/Admin/DashboardView.vue");
const Feedbacks = () => import("@/views/Admin/FeedbacksView.vue");
const Reports = () => import("@/views/Admin/ReportsView.vue");
const ApplicationsView = () => import("@/views/Admin/ApplicationsView.vue");
const UserAccountsView = () => import("@/views/Admin/UserAccountsView.vue");
const Sessions = () => import("@/views/Admin/SessionsView.vue");
const Subjects = () => import("@/views/Admin/SubjectsView.vue");
const TestResults = () => import("@/views/Admin/TestResultsView.vue");
const Archives = () => import("@/views/Admin/ArchivesView.vue");
const TrustedDevices = () => import("@/views/Admin/TrustedDevicesView.vue");
const AuditLogs = () => import("@/views/Admin/AuditLogsView.vue");

import StudentProfileView from "@/views/Student/StudentProfileView.vue";
import TutorProfileView from "@/views/Tutor/TutorProfileView.vue";
import BookSessionView from "@/views/Student/BookSessionView.vue";
import BookingConfirmationView from "@/views/Student/BookingConfirmationView.vue";
import FileAFeedbackView from "@/views/FileAFeedbackView.vue";
import OAuthCallbackView from "@/views/OAuthCallbackView.vue";
import NotFoundView from "@/views/NonLogged-In/NotFoundView.vue";
import TwoFactorView from "@/views/TwoFactorView.vue";
import TrustDeviceView from "@/views/TrustDeviceView.vue";
import ManageDevicesView from "@/views/ManageDevicesView.vue";

// Store scroll positions for visited pages during the session
const scrollPositions = new Map<string, number>();

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public site routes
    { path: "/", name: "home", component: HomeView },
    { path: "/login", name: "login", component: LoginView },
    {
      path: "/password-reset-request",
      name: "password-reset-request",
      component: PasswordResetRequestView,
    },
    { path: "/password-reset", name: "password-reset", component: PasswordResetView },
    { path: "/student-register", name: "student-register", component: StudentRegisterView },
    { path: "/tutor-register", name: "tutor-register", component: TutorRegisterView },
    { path: "/about", name: "about", component: AboutView },
    { path: "/register-role", name: "register-role", component: RegisterRoleView },
    { path: "/privacy-policy", name: "privacy-policy", component: PrivacyPolicyView },
    { path: "/terms-conditions", name: "terms-conditions", component: TermsandConditionsView },

    // Student routes
    {
      path: "/student-session",
      name: "student-session",
      component: StudentSessionView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/find-tutors",
      name: "find-tutors",
      component: FindTutorsView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/sessions",
      name: "student-sessions",
      component: StudentSessionView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/study-materials",
      name: "student-study-materials",
      component: StudentStudyMaterialsView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student-messages",
      name: "student-messages",
      component: StudentMessagesView,
      meta: { requiresStudentAccess: true },
    },
    { path: "/email-verification", name: "email-verification", component: EmailVerificationView },

    // 2FA and Trusted Device routes (for admin/superadmin)
    {
      path: "/2fa/:twoFAId",
      name: "TwoFactor",
      component: TwoFactorView,
      meta: { requiresAuth: true },
    },
    {
      path: "/trust-device",
      name: "TrustDevice",
      component: TrustDeviceView,
      meta: { requiresAuth: true },
    },
    {
      path: "/manage-devices",
      name: "ManageDevices",
      component: ManageDevicesView,
      meta: { requiresAuth: true },
    },
    {
      path: "/student/ai-generated-study-material",
      name: "ai-generated-study-material",
      component: AIGeneratedStudyMaterialView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/ai-generated-flashcards",
      name: "ai-generated-flashcards",
      component: AIGeneratedFlashcardsView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/book-session/:tutorId",
      name: "book-session-with-tutor",
      component: BookSessionView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/booking-confirmation",
      name: "booking-confirmation",
      component: BookingConfirmationView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/student/application-status",
      name: "student-application-status",
      component: () => import("@/views/Student/StudentApplicationStatusView.vue"),
      meta: { requiresAuth: true, role: "student" },
    },
    {
      path: "/student/rejection",
      name: "student-rejection",
      component: () => import("@/views/Student/StudentRejectionView.vue"),
      meta: { requiresAuth: true, role: "student" },
    },
    // Tutor routes
    {
      path: "/tutor-session",
      name: "tutor-session",
      component: TutorSessionView,
      meta: { requiresTutorAccess: true },
    }, // Legacy route - redirect handled
    {
      path: "/tutor/sessions",
      name: "tutor-sessions",
      component: TutorSessionView,
      meta: { requiresTutorAccess: true },
    },
    {
      path: "/oauth-callback",
      name: "oauth-callback",
      component: OAuthCallbackView,
    },
    {
      path: "/tutor/application-status",
      name: "tutor-application-status",
      component: TutorApplicationStatusView,
      meta: { requiresAuth: true },
    },
    {
      path: "/tutor/resubmission",
      name: "tutor-resubmission",
      component: ResubmissionView,
      meta: { requiresAuth: true },
    },
    {
      path: "/student/profile",
      name: "student-profile",
      component: StudentProfileView,
      meta: { requiresStudentAccess: true },
    },
    {
      path: "/tutor/profile",
      name: "tutor-profile",
      component: TutorProfileView,
      meta: { requiresTutorAccess: true },
    },
    {
      path: "/tutor-messages",
      name: "tutor-messages",
      component: TutorMessagesView,
      meta: { requiresTutorAccess: true },
    },
    {
      path: "/feedback/:sessionId?",
      name: "file-a-feedback",
      component: FileAFeedbackView,
      meta: { requiresAuth: true },
    },

    // Admin routes
    {
      path: "/admin",
      name: "admin-dashboard",
      component: Dashboard,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/dashboard",
      name: "admin-dashboard-alt",
      component: Dashboard,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/superadmin",
      name: "admin-superadmin",
      component: Dashboard,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/feedbacks",
      name: "admin-feedbacks",
      component: Feedbacks,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/reports",
      name: "admin-reports",
      component: Reports,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/tutor-applications",
      redirect: "/admin/applications",
    },
    {
      path: "/admin/applications",
      name: "admin-applications",
      component: ApplicationsView,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/user-accounts",
      name: "admin-user-accounts",
      component: UserAccountsView,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/sessions",
      name: "admin-sessions",
      component: Sessions,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/subjects",
      name: "admin-subjects",
      component: Subjects,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/test-results",
      name: "admin-test-results",
      component: TestResults,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/archives",
      name: "admin-archives",
      component: Archives,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/trusted-devices",
      name: "admin-trusted-devices",
      component: TrustedDevices,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },
    {
      path: "/admin/audit-logs",
      name: "admin-audit-logs",
      component: AuditLogs,
      meta: { requiresAuth: true, requiresAdminAccess: true },
    },

    // Validator routes - Reuse existing components with ValidatorHeader
    {
      path: "/validator",
      redirect: "/validator/ai-testing",
    },
    {
      path: "/validator/tutor-applications",
      name: "validator-tutor-applications",
      component: () => import("@/views/Validator/ValidatorTutorApplicationsView.vue"),
      meta: { requiresAuth: true, requiresValidatorAccess: true },
    },
    {
      path: "/validator/ai-testing",
      name: "validator-ai-testing",
      component: () => import("@/views/Validator/ValidatorAITestingView.vue"),
      meta: { requiresAuth: true, requiresValidatorAccess: true },
    },
    {
      path: "/validator/post-tests",
      name: "validator-post-tests",
      component: () => import("@/views/Validator/ValidatorPostTestsView.vue"),
      meta: { requiresAuth: true, requiresValidatorAccess: true },
    },
    {
      path: "/validator/pre-tests",
      name: "validator-pre-tests",
      component: () => import("@/views/Validator/ValidatorPreTestsView.vue"),
      meta: { requiresAuth: true, requiresValidatorAccess: true },
    },
    {
      path: "/validator/ai-generated-study-material",
      name: "validator-ai-generated-study-material",
      component: () => import("@/views/Validator/ValidatorAIGeneratedStudyMaterialView.vue"),
      meta: { requiresAuth: true, requiresValidatorAccess: true },
    },
    {
      path: "/validator/ai-generated-flashcards",
      name: "validator-ai-generated-flashcards",
      component: () => import("@/views/Validator/ValidatorAIGeneratedFlashcardsView.vue"),
      meta: { requiresAuth: true, requiresValidatorAccess: true },
    },

    // 404 catch-all route - must be last
    { path: "/:pathMatch(.*)*", name: "not-found", component: NotFoundView },
  ],
  linkActiveClass: "router-link-active",
  linkExactActiveClass: "router-link-exact-active",
  scrollBehavior(to, from, savedPosition) {
    // If the browser has a saved position (e.g., back/forward navigation), use it immediately
    if (savedPosition) {
      return savedPosition;
    }

    // If navigating to a hash (anchor), scroll to that element
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }

    // Check if we have a stored scroll position for this route
    const savedScrollPosition = scrollPositions.get(to.path);

    if (savedScrollPosition !== undefined) {
      // Restore immediately - no delay for instant feel
      return { top: savedScrollPosition, left: 0, behavior: "instant" };
    }

    // For new visits, scroll to the top instantly
    return { top: 0, left: 0 };
  },
});

// Save scroll position before leaving a route
router.beforeEach((to, from, next) => {
  // Save the current scroll position when leaving a page
  if (from.path && from.path !== "/") {
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    scrollPositions.set(from.path, scrollTop);
  }
  next();
});

// Finish loading when route is ready
router.afterEach(() => {
  const { finishLoading } = useRouteLoading();
  // Small delay to ensure component has started mounting
  setTimeout(() => {
    finishLoading();
  }, 100);
});

// Route guard to check tutor and student access using Firebase
router.beforeEach(async (to, from, next) => {
  // Import secure logger
  const { logError, logWarn, logDebug } = await import("@/utils/secureLogger");

  // Define public routes that logged-in users should be redirected away from
  const publicRoutes = [
    "/",
    "/login",
    "/about",
    "/privacy-policy",
    "/terms-conditions",
    "/register-role",
    "/student-register",
    "/tutor-register",
  ];

  // Check if the user is trying to access a public route
  if (publicRoutes.includes(to.path)) {
    try {
      const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");
      const { twoFactorAuthService } = await import("@/services/twoFactorAuthService");

      let currentUser = auth.currentUser;

      if (!currentUser) {
        // Wait for auth state to initialize
        await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });
        currentUser = auth.currentUser;
      }

      // If user is logged in, redirect them to their dashboard
      // But first check if admin has pending 2FA
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userType = userData.userType || "student";
          const applicationStatus = userData.applicationStatus || "pending";
          const profileStatus = userData.profileStatus || "pending_approval";

          // For admins, check if they have pending 2FA first
          if (userType === "admin" || userType === "superadmin") {
            const pending2FA = twoFactorAuthService.hasPending2FA();

            if (pending2FA) {
              logWarn("Admin with pending 2FA trying to access public route - redirecting to 2FA", {
                twoFAId: pending2FA.twoFAId,
              });
              next({
                name: "TwoFactor",
                params: { twoFAId: pending2FA.twoFAId },
                query: { expiresAt: pending2FA.expiresAt },
              });
              return;
            }
          }
          // Check if user's email is verified
          const isEmailVerified = userData.isEmailVerified || currentUser.emailVerified;

          // Admins and superadmins don't need email verification (they have 2FA instead)
          const isAdmin = userType === "admin" || userType === "superadmin";

          // If user's email is not verified and they're trying to access registration pages,
          // allow them through (they might need to change their email)
          if (
            !isEmailVerified &&
            !isAdmin &&
            (to.path === "/tutor-register" || to.path === "/student-register")
          ) {
            logDebug("Allowing unverified user to access registration page to change email");
            next();
            return;
          }

          // If user's email is not verified and they're trying to access a public route
          // (like home, about, etc.), sign them out and allow navigation
          if (!isEmailVerified && !isAdmin && publicRoutes.includes(to.path)) {
            logDebug("Unverified user navigating to public route - signing them out");
            const { signOut } = await import("@/firebase");
            await signOut(auth);
            logDebug("User signed out, allowing navigation to", to.path);
            next();
            return;
          }

          // If user's email is not verified (and not admin), redirect them to email verification page
          if (!isEmailVerified && !isAdmin) {
            logDebug("Email not verified - redirecting to verification page");
            next({
              path: "/email-verification",
              query: {
                email: userData.email || currentUser.email,
                from: userType === "tutor" ? "tutor-register" : "student-register",
              },
            });
            return;
          }

          logDebug("Redirecting logged-in user from public route", { userType, fromPath: to.path });

          // Redirect based on user type and status
          if (userType === "admin" || userType === "superadmin") {
            next("/admin/dashboard");
            return;
          } else if (userType === "validator") {
            next("/validator/ai-testing");
            return;
          } else if (userType === "tutor") {
            if (applicationStatus === "approved" && profileStatus === "approved") {
              next("/tutor/sessions");
              return;
            } else {
              next("/tutor/application-status");
              return;
            }
          } else if (userType === "student") {
            if (
              applicationStatus === "approved" ||
              applicationStatus === "active" ||
              applicationStatus === "Active"
            ) {
              next("/student/find-tutors");
              return;
            } else if (applicationStatus === "rejected") {
              next("/student/rejection");
              return;
            } else {
              next("/student/application-status");
              return;
            }
          }
        }
      }
    } catch (error) {
      logError("Error checking auth for public route redirect", error);
      // On error, allow access to public route
    }
  }

  // Check if route requires admin access
  if (to.meta.requiresAdminAccess) {
    try {
      const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");
      const { twoFactorAuthService } = await import("@/services/twoFactorAuthService");

      let currentUser = auth.currentUser;

      if (!currentUser) {
        await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });
        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        logWarn("Unauthorized admin access attempt - no user authenticated");
        next("/login");
        return;
      }

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        logWarn("Unauthorized admin access attempt - user document not found");
        next("/login");
        return;
      }

      const userData = userDoc.data();
      const userType = userData.userType || "";

      // Allow validators to access ONLY /admin/applications
      if (userType === "validator" && to.path === "/admin/applications") {
        logDebug("Validator access granted to applications page", { userType });
        next();
        return;
      }

      if (userType === "admin" || userType === "superadmin") {
        // Check if 2FA verification is pending
        const pending2FA = twoFactorAuthService.hasPending2FA();

        if (pending2FA) {
          logWarn("Admin has pending 2FA - redirecting to verification page", {
            twoFAId: pending2FA.twoFAId,
          });
          next({
            name: "TwoFactor",
            params: { twoFAId: pending2FA.twoFAId },
            query: { expiresAt: pending2FA.expiresAt },
          });
          return;
        }

        // Check if current device is trusted
        const { trustedDeviceService } = await import("@/services/trustedDeviceService");
        const isDeviceTrusted = await trustedDeviceService.isCurrentDeviceTrusted();

        if (!isDeviceTrusted) {
          logWarn("Admin access blocked - device not trusted", {
            userId: currentUser.uid,
          });

          // Check if we have a pending 2FA session to redirect back to
          const storedTwoFAId = sessionStorage.getItem("pending2FAId");

          if (storedTwoFAId) {
            // Redirect to trust device page
            next({
              path: "/trust-device",
              query: { twoFAId: storedTwoFAId },
            });
          } else {
            // No 2FA session, redirect to manage devices
            next("/manage-devices");
          }
          return;
        }

        logDebug("Admin access granted", { userType });
        next();
      } else {
        logWarn("Unauthorized admin access attempt", { userType });
        next({ name: "not-found" });
      }
    } catch (error) {
      logError("Error checking admin access", error);
      next("/login");
    }
  } else if (to.meta.requiresValidatorAccess) {
    try {
      const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");
      let currentUser = auth.currentUser;
      if (!currentUser) {
        await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });
        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        next("/login");
        return;
      }

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        next("/login");
        return;
      }
      const userData = userDoc.data();
      const userType = userData.userType || "";
      if (userType === "validator") {
        next();
      } else {
        const { logWarn } = await import("@/utils/secureLogger");
        logWarn("Unauthorized validator access attempt", { userType });
        next({ name: "not-found" });
      }
    } catch (error) {
      const { logError } = await import("@/utils/secureLogger");
      logError("Error checking validator access", error);
      next("/login");
    }
  } else if (to.meta.requiresTutorAccess) {
    try {
      // Import Firebase functions dynamically
      const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");

      // Check if user is authenticated
      let currentUser = auth.currentUser;

      if (!currentUser) {
        // Wait for auth state to initialize
        await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });

        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        // No authenticated user, redirect to login
        next("/login");
        return;
      }

      // Get user data from Firestore
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // User document doesn't exist, redirect to login
        next("/login");
        return;
      }

      const userData = userDoc.data();
      const userType = userData.userType || "student";
      const applicationStatus = userData.applicationStatus || "pending";
      const profileStatus = userData.profileStatus || "pending_approval";

      // Check if user can access tutor dashboard
      const canAccess =
        userType === "tutor" && applicationStatus === "approved" && profileStatus === "approved";

      if (canAccess) {
        // User is approved tutor, allow access
        next();
      } else if (userType === "tutor") {
        // User is tutor but not approved, redirect to application status
        next("/tutor/application-status");
      } else {
        // User is not a tutor, show 404
        logWarn("Unauthorized tutor access attempt", { userType });
        next({ name: "not-found" });
      }
    } catch (error) {
      logError("Error checking tutor access", error);

      // ✅ CRITICAL FIX: Check if error is a Firestore permission error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorCode = (error as any)?.code;

      if (
        errorCode === "permission-denied" ||
        errorMessage.includes("permission-denied") ||
        errorMessage.includes("Missing or insufficient permissions")
      ) {
        logWarn("Firestore permission error during navigation guard - allowing access", {
          error: errorMessage.substring(0, 100),
          note: "Component-level access control will handle validation",
        });
        next();
      } else {
        next("/login");
      }
    }
  } else if (to.meta.requiresStudentAccess) {
    // Check if route requires student access
    try {
      // Import Firebase functions dynamically
      const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");

      // Check if user is authenticated
      let currentUser = auth.currentUser;

      if (!currentUser) {
        // Wait for auth state to initialize
        await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });

        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        // No authenticated user, redirect to login
        next("/login");
        return;
      }

      // Get user data from Firestore
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // User document doesn't exist, redirect to login
        next("/login");
        return;
      }

      const userData = userDoc.data();
      const userType = userData.userType || "student";
      const applicationStatus = userData.applicationStatus || "pending";

      // Check if user can access student dashboard
      // Allow access for approved students and those who need to resubmit documents (limited access with alert)
      const canAccess =
        userType === "student" &&
        (applicationStatus === "approved" ||
          applicationStatus === "active" ||
          applicationStatus === "Active" ||
          applicationStatus === "document_issue" ||
          applicationStatus === "resubmitted_under_review");

      if (canAccess) {
        // User is approved student or has document issues (will see alert), allow access
        next();
      } else if (userType === "student") {
        // User is student but not approved
        if (applicationStatus === "rejected") {
          // Redirect rejected students to dedicated rejection page
          next("/student/rejection");
        } else {
          // Redirect pending students to application status
          next("/student/application-status");
        }
      } else {
        // User is not a student, show 404
        logWarn("Unauthorized student access attempt", { userType });
        next({ name: "not-found" });
      }
    } catch (error) {
      logError("Error checking student access", error);

      // ✅ CRITICAL FIX: Check if error is a Firestore permission error
      // If so, allow navigation anyway (the component will handle access control)
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorCode = (error as any)?.code;

      if (
        errorCode === "permission-denied" ||
        errorMessage.includes("permission-denied") ||
        errorMessage.includes("Missing or insufficient permissions")
      ) {
        logWarn("Firestore permission error during navigation guard - allowing access", {
          error: errorMessage.substring(0, 100),
          note: "Component-level access control will handle validation",
        });
        // Allow navigation - component will handle access control
        next();
      } else {
        // For other errors, redirect to login
        next("/login");
      }
    }
  } else if (to.meta.requiresAuth) {
    // Check if route requires basic authentication (no specific role)
    try {
      const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");

      let currentUser = auth.currentUser;

      if (!currentUser) {
        await new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });
        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        logWarn("Authentication required - redirecting to login");
        next("/login");
        return;
      }

      // Check if email is verified (skip for admins)
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userType = userData.userType || "";
        const isAdmin = userType === "admin" || userType === "superadmin";
        const isEmailVerified = userData.isEmailVerified || currentUser.emailVerified;

        // Only require email verification for non-admin users
        if (!isEmailVerified && !isAdmin) {
          logWarn("Email not verified - redirecting to verification page");
          next({
            path: "/email-verification",
            query: {
              email: userData.email || currentUser.email,
              from: userData.userType === "tutor" ? "tutor-register" : "student-register",
            },
          });
          return;
        } else if (!isEmailVerified && isAdmin) {
          logDebug("Admin user - skipping email verification, will use 2FA");
        }
      }

      // User is authenticated and verified, allow access
      next();
    } catch (error) {
      logError("Error checking authentication", error);
      next("/login");
    }
  } else {
    // Route doesn't require special access, proceed normally
    next();
  }
});

// After each navigation, set cache control headers for authenticated pages
router.afterEach((to) => {
  // Check if the route requires authentication
  const requiresAuth =
    to.meta.requiresAuth ||
    to.meta.requiresAdminAccess ||
    to.meta.requiresStudentAccess ||
    to.meta.requiresTutorAccess;

  if (requiresAuth) {
    // Disable browser cache for authenticated pages
    // This prevents the back button from showing cached content after logout
    if (typeof document !== "undefined") {
      const metaTags = [
        { httpEquiv: "Cache-Control", content: "no-cache, no-store, must-revalidate" },
        { httpEquiv: "Pragma", content: "no-cache" },
        { httpEquiv: "Expires", content: "0" },
      ];

      metaTags.forEach((tag) => {
        let metaElement = document.querySelector(`meta[http-equiv="${tag.httpEquiv}"]`);
        if (!metaElement) {
          metaElement = document.createElement("meta");
          metaElement.setAttribute("http-equiv", tag.httpEquiv);
          document.head.appendChild(metaElement);
        }
        metaElement.setAttribute("content", tag.content);
      });
    }
  }
});

// Listen for page visibility changes to revalidate authentication
// This handles the case where user navigates back using browser buttons
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", async (event) => {
    // Check if page was loaded from cache (BFCache)
    if (event.persisted) {
      // Page was restored from BFCache, need to revalidate auth
      const { auth } = await import("@/firebase");
      const { logWarn } = await import("@/utils/secureLogger");

      const currentRoute = router.currentRoute.value;
      const requiresAuth =
        currentRoute.meta.requiresAuth ||
        currentRoute.meta.requiresAdminAccess ||
        currentRoute.meta.requiresStudentAccess ||
        currentRoute.meta.requiresTutorAccess;

      if (requiresAuth && !auth.currentUser) {
        logWarn("Page restored from BFCache without authentication - redirecting to login");
        router.replace("/login");
      }
    }
  });

  // Also handle visibility change events
  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible") {
      const { auth } = await import("@/firebase");
      const { logDebug } = await import("@/utils/secureLogger");

      const currentRoute = router.currentRoute.value;
      const requiresAuth =
        currentRoute.meta.requiresAuth ||
        currentRoute.meta.requiresAdminAccess ||
        currentRoute.meta.requiresStudentAccess ||
        currentRoute.meta.requiresTutorAccess;

      if (requiresAuth && !auth.currentUser) {
        logDebug("Page became visible without authentication - redirecting to login");
        router.replace("/login");
      }
    }
  });
}

export default router;
